import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Depot } from 'depoto-core/src/entities'
import { AutomaticOrdersSettings, CoreService, HsApiService, NotificationService } from '../../services'
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'
import { Address } from 'depoto-core'

@Component({
  selector: 'app-automatic-orders',
  templateUrl: 'automatic-orders.component.html',
})
export class AutomaticOrdersComponent implements OnInit, CheckDataChanges {
  automaticOrdersSettings: AutomaticOrdersSettings
  depots: Depot[] = []
  loading = true
  submitted = false
  automatizationLevelOptions = [
    { id: 'none', name: 'automatic-orders.levels.none' },
    { id: 'manual-confirmation', name: 'automatic-orders.levels.manual-confirmation' },
    { id: 'automatic', name: 'automatic-orders.levels.automatic' },
  ]
  frequencyOptions = [
    { id: 'weekly', name: 'automatic-orders.frequency.weekly' },
    { id: '2weeks', name: 'automatic-orders.frequency.2weeks' },
    { id: 'monthly', name: 'automatic-orders.frequency.monthly' },
  ]
  weekDayOptions = [
    { id: 'monday', name: 'automatic-orders.weekDay.monday' },
    { id: 'tuesday', name: 'automatic-orders.weekDay.tuesday' },
    { id: 'wednesday', name: 'automatic-orders.weekDay.wednesday' },
    { id: 'thursday', name: 'automatic-orders.weekDay.thursday' },
    { id: 'friday', name: 'automatic-orders.weekDay.friday' },
    { id: 'saturday', name: 'automatic-orders.weekDay.saturday' },
    { id: 'sunday', name: 'automatic-orders.weekDay.sunday' },
  ]

  automaticOrdersSettingsForm = new UntypedFormGroup({
    // automatizationLevel: new UntypedFormControl({ value: 'manual', disabled: false }, [Validators.required]),
    // frequency: new UntypedFormControl({ value: 'weekly', disabled: false }, [Validators.required]),
    // weekDay: new UntypedFormControl({ value: 'monday', disabled: false }, []),
    monthDay: new UntypedFormControl({ value: 1, disabled: false }, []),
    notificationEmail: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    depots: new UntypedFormControl({ value: [], disabled: false }, []),
  })
  customerCode: string
  addresses: Address[]
  depotAddresses: any = {}

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private hsApi: HsApiService,
    private core: CoreService,
  ) {}

  ngOnInit() {
    this.refreshData()
  }

  fillFormValues() {
    this.automaticOrdersSettingsForm.setValue({
      // automatizationLevel: this.automaticOrdersSettings.automatizationLevel || 'manual',
      // frequency: this.automaticOrdersSettings.frequency || 'weekly',
      // weekDay: this.automaticOrdersSettings.weekDay || 'monday',
      monthDay: this.automaticOrdersSettings.monthDay || '1',
      notificationEmail: this.automaticOrdersSettings.notificationEmail || '',
      depots: this.automaticOrdersSettings.depots || [],
    })
  }

  getFormValue() {
    // this.automaticOrdersSettings.automatizationLevel = this.automaticOrdersSettingsForm.value.automatizationLevel
    // this.automaticOrdersSettings.frequency = this.automaticOrdersSettingsForm.value.frequency
    // this.automaticOrdersSettings.weekDay = this.automaticOrdersSettingsForm.value.weekDay
    this.automaticOrdersSettings.monthDay = Number(this.automaticOrdersSettingsForm.value.monthDay)
    this.automaticOrdersSettings.notificationEmail = this.automaticOrdersSettingsForm.value.notificationEmail
    this.automaticOrdersSettings.depots = this.automaticOrdersSettingsForm.value.depots
  }

  hasUnsavedDataChanges(): boolean {
    return false
  }

  async refreshData() {
    this.loading = true
    this.automaticOrdersSettings = this.core.getAutomaticOrdersSettings()
    this.customerCode = this.core.services.user.user?.company?.externalId
    await this.fetchAddresses()
    this.depots = this.core.lists.depots
    this.depotAddresses = {}
    this.depots.forEach(d => {
      this.depotAddresses[d.id] =
        this.automaticOrdersSettings.depots.find(depot => depot.id === d.id)?.addressId || null
    })
    this.fillFormValues()
    this.loading = false
  }

  async updateData() {
    this.submitted = true
    if (this.automaticOrdersSettingsForm.invalid) {
      return
    }
    try {
      this.loading = true
      this.getFormValue()
      const company = this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      const customOptions = this.core.getCustomOptions()
      customOptions.automaticOrdersSettings = { ...this.automaticOrdersSettings }
      customOptions.automaticOrdersSettings.depots = customOptions.automaticOrdersSettings.depots.map(d => ({
        id: d.id,
        addressId: this.depotAddresses[d.id],
      }))
      const result = await this.core.services.company.update({ id: company.id, customOptions })
      this.notificationService.success(this.translateService.instant('alert.automatic-orders.updated'))
      const userEmail = this.core.services.storage.getSync(this.core.services.storage.keys.auth.email)
      await this.core.services.user.setCurrentUser(userEmail)
      // return this.refreshData() // todo this fucks up addresses on depots on first refresh. subsequent refreshes ok. check if not refreshing breaks smth
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  toggleDepot(depot: Depot, event: boolean) {
    if (event) {
      this.automaticOrdersSettingsForm.value.depots.push(depot)
    } else {
      this.automaticOrdersSettingsForm.value.depots.forEach((d: Depot, i: number) => {
        if (depot.id === d.id) {
          this.automaticOrdersSettingsForm.value.depots.splice(i, 1)
        }
      })
    }
  }

  isChosenDepot(depot: Depot): boolean {
    let exists = false
    this.automaticOrdersSettingsForm.value.depots.forEach((d: Depot) => {
      if (d.id === depot.id) {
        exists = true
        return
      }
    })
    return exists
  }

  updateDepotAddress(depotId: number, addressId: string) {
    this.depotAddresses[depotId] = Number(addressId)
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.automaticOrdersSettingsForm.controls
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/home'])
    }
  }

  async fetchAddresses() {
    const res = await this.hsApi.getHsAddresses()
    this.addresses = res?.map(a => ({
      id: a.id,
      name: `${a.street} ${a.houseNumber}, ${a.zip} ${a.city}`,
      address: a,
    }))
  }
}
